import React, { CSSProperties, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import EditProjectDialog from '../../../components/dialogs/EditProjectDialog/EditProjectDialog';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import { HealthCheck, ModelSortInput, Tag, UserRoleEnum, useGetModelsQuery, useGetClientQuery, useToggleProjectPrivateMutation } from '../../../generated/graphql';
import { useCurrentUser } from '../../../App.hooks';
import { newGuid, ticksToDate } from '../../../Utils';
import { ProjectContext } from '../Projects.provider';
import Worksets from '../Worksets/Worksets';
import Standards from '../Standards/Standards';
import Reports from '../Reports/Reports';
import SearchForApprovedContentsDialog from '../../../components/dialogs/SearchForApprovedContentsDialog/SearchForApprovedContentsDialog';
import DeleteApprovedContentsDialog from '../../../components/dialogs/DeleteApprovedContentsDialog/DeleteApprovedContentsDialog';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';
import ImageViewerDialog from '../../../components/dialogs/ImageViewerDialog/ImageViewerDialog';
import { Link } from 'react-router-dom';
import DeleteProjectTagDialog from '../../../components/dialogs/DeleteProjectTagDialog/DeleteProjectTagDialog';
import DeleteProjectHealthChecksDialog from '../../../components/dialogs/DeleteProjectHealthChecksDialog/DeleteProjectHealthChecksDialog';
import DeleteModelsDialog from '../../../components/dialogs/DeleteModelsDialog/DeleteModelsDialog';
import ShowOnRole from '../../../components/main/ShowOnRole/ShowOnRole';
import DonutChart from '../../../components/main/DonutChart/DonutChart';
import { Eye, EyeOff, Pencil, Plus, Trash2, Search, Box, Package, FileText, Activity, CheckCircle2, AlertTriangle, XCircle, Layers, BookOpen, BarChart3 } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import TagBadge from '../../../components/main/TagBadge/TagBadge';

/* ------------------------------------------------------------------ */
/*  Types & helpers for project-level health aggregation              */
/* ------------------------------------------------------------------ */
type HealthData = {
    Name: string;
    Group: string;
    Limit: number;
    Value: number;
    AdditionalData: string;
};

type ModelHealthSummary = {
    modelId: string;
    modelName: string;
    applicationName: string;
    passing: number;
    warning: number;
    failing: number;
    total: number;
    score: number; // 0-100, percentage of checks passing
};

const classifyHealth = (value: number, limit: number) => {
    if (limit <= 0) return 'na';
    const pct = value / limit;
    if (pct <= 1.0) return 'passing';
    if (pct < 1.3) return 'warning';
    return 'failing';
};

/* ------------------------------------------------------------------ */
/*  Stat card — small KPI tile (matches Model page style)             */
/* ------------------------------------------------------------------ */
const StatCard = ({ label, value, icon: Icon, color }: {
    label: string; value: string | number;
    icon: React.ElementType; color: string;
}) => (
    <div className="card p-4 flex items-center gap-3 min-w-[160px]">
        <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}18`, color }}
        >
            <Icon size={18} />
        </div>
        <div>
            <div className="text-xl font-bold" style={{ color: 'var(--text-primary)', lineHeight: 1.2 }}>{value}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</div>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Health badge for model rows                                       */
/* ------------------------------------------------------------------ */
const ScoreBadge = ({ score }: { score: number }) => {
    if (score >= 80) return <span className="badge badge-success"><CheckCircle2 size={11} className="mr-0.5" /> {score}%</span>;
    if (score >= 50) return <span className="badge badge-warning"><AlertTriangle size={11} className="mr-0.5" /> {score}%</span>;
    return <span className="badge badge-danger"><XCircle size={11} className="mr-0.5" /> {score}%</span>;
};

const CustomPropertiesCell = ({ row }: { row: any }) => {
    const object = row?.customProperties && JSON.parse(row.customProperties);
    return (
        <div className="flex flex-wrap gap-1">
            {object && Object.keys(object).map(key => (
                <span key={key} className="badge badge-default text-xs">{key}: {object[key].toString()}</span>
            ))}
        </div>
    );
};

const ImageViewerButton = ({ link, title }: { link: string, title: string; }) => {
    const [imageViewerVisible, setImageViewerVisible] = useState(false);

    return (<>
        <button className="btn btn-ghost p-1" onClick={() => setImageViewerVisible(true)}>
            <Search size={14} />
        </button>
        {imageViewerVisible && <ImageViewerDialog title={title} link={link} visible={imageViewerVisible} onClose={() => setImageViewerVisible(false)} />}
    </>);
};

const StandardDetailsTab = ({ projectId, libraryId, contents, loading, isAdmin, onRefresh }: { isAdmin: boolean, projectId: string, libraryId: string, contents: any[], loading: boolean | undefined, onRefresh: () => void; }) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [searchContentsVisible, setSearchContentsVisible] = useState(false);
    const [deleteContentsVisible, setDeleteContentsVisible] = useState(false);

    const handleOnClose = () => {
        setSearchContentsVisible(false);
        setDeleteContentsVisible(false);
        onRefresh();
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={onRefresh} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button className="btn btn-ghost text-sm" onClick={() => setSearchContentsVisible(true)}>
                        <Plus size={15} /> Add Details
                    </button>
                    {selectedIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteContentsVisible(true)}>
                                <Trash2 size={15} /> Delete
                            </button>
                        </>
                    )}
                </ShowOnRole>
            </div>
            <div className="flex flex-col flex-1 overflow-hidden">
                <DataTableNew
                    sortingMode="client"
                    isLoading={loading || false}
                    data={contents}
                    showTags
                    onSelected={(ids) => setSelectedIds(ids)}
                    columns={[
                        {
                            id: 'action',
                            name: '',
                            width: '55px',
                            render: (row: any) => row?.thumbnail?.downloadUrl
                                ? <ImageViewerButton title={row.name} link={row.thumbnail.downloadUrl} />
                                : null
                        }
                    ]}
                />
            </div>
            {searchContentsVisible && <SearchForApprovedContentsDialog projectId={projectId} libraryId={libraryId} visible={searchContentsVisible} onClose={handleOnClose} />}
            {deleteContentsVisible && <DeleteApprovedContentsDialog projectId={projectId} ids={selectedIds} visible={deleteContentsVisible} onClose={handleOnClose} />}
        </div>
    );
};

type ProjectOverviewProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const ProjectOverview = (props: ProjectOverviewProps) => {
    const project = useContext(ProjectContext);
    const [activeTab, setActiveTab] = useState<'models' | 'standards' | 'reports' | 'worksets' | 'standard-details'>('models');
    const [searchModel, setSearchModel] = useState("");
    const [editProjectVisible, setEditProjectVisible] = useState(false);
    const [deleteProjectTagVisible, setDeleteProjectTagVisible] = useState(false);
    const [selectedTagId, setselectedTagId] = useState("");
    const [deleteProjectHealthCheckVisible, setDeleteProjectHealthCheckVisible] = useState(false);
    const [selectedHealthCheckId, setSelectedHealthCheckId] = useState("");
    const [deleteModelVisible, setDeleteModelVisible] = useState(false);
    const [selectedModelId, setSelectedModelId] = useState("");
    const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
    const [modelSort, setModelSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });
    const [togglePrivate, togglePrivateResult] = useToggleProjectPrivateMutation();

    const modelData = useGetModelsQuery({
        variables: {
            projectId: project?.data?.Project?.id,
            contains: searchModel,
            order: createGraphQLOrder<ModelSortInput>(modelSort),
        }
    });

    // Auto-fetch all remaining pages of models
    useEffect(() => {
        if (modelData.loading) return;
        if (!modelData.data?.ModelList?.pageInfo?.hasNextPage) return;
        modelData.fetchMore({
            variables: {
                projectId: project?.data?.Project?.id,
                contains: searchModel,
                after: modelData.data.ModelList.pageInfo.endCursor,
                order: createGraphQLOrder<ModelSortInput>(modelSort),
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) return previousResult;
                const prev = previousResult.ModelList?.nodes || [];
                const next = fetchMoreResult.ModelList?.nodes || [];
                return {
                    ...previousResult,
                    ModelList: {
                        ...fetchMoreResult.ModelList,
                        nodes: [...prev, ...next],
                        pageInfo: fetchMoreResult.ModelList?.pageInfo as any
                    }
                };
            }
        });
    }, [modelData.data?.ModelList?.pageInfo?.endCursor, modelData.loading, modelSort]);

    /* -------------------------------------------------------------- */
    /*  Aggregate health results from all models at the project level */
    /* -------------------------------------------------------------- */
    const models = modelData.data?.ModelList?.nodes as any[] || [];

    const { projectHealthSummary, modelSummaries } = useMemo(() => {
        let totalPassing = 0, totalWarning = 0, totalFailing = 0, totalChecks = 0;
        const summaries: ModelHealthSummary[] = [];

        for (const m of models) {
            if (!m?.healthResults) continue;
            try {
                const results: HealthData[] = JSON.parse(m.healthResults);
                let passing = 0, warning = 0, failing = 0;
                for (const r of results) {
                    const status = classifyHealth(r.Value, r.Limit);
                    if (status === 'passing') passing++;
                    else if (status === 'warning') warning++;
                    else if (status === 'failing') failing++;
                }
                const total = passing + warning + failing;
                totalPassing += passing;
                totalWarning += warning;
                totalFailing += failing;
                totalChecks += total;
                summaries.push({
                    modelId: m.id,
                    modelName: m.name || 'Unnamed Model',
                    applicationName: m.applicationName || '',
                    passing, warning, failing, total,
                    score: total > 0 ? Math.round((passing / total) * 100) : 0,
                });
            } catch { /* skip models with invalid healthResults */ }
        }

        return {
            projectHealthSummary: {
                totalModels: models.length,
                modelsWithHealth: summaries.length,
                totalChecks, totalPassing, totalWarning, totalFailing,
                overallScore: totalChecks > 0 ? Math.round((totalPassing / totalChecks) * 100) : 0,
            },
            modelSummaries: summaries.sort((a, b) => a.score - b.score), // worst first
        };
    }, [models]);

    const togglePrivateHandle = async () => {
        await togglePrivate({
            variables: {
                id: project?.data?.Project?.id
            }
        });
        project?.refetch();
    };

    const handleOnClose = () => {
        setEditProjectVisible(false);
        setDeleteProjectTagVisible(false);
        setDeleteProjectHealthCheckVisible(false);
        setDeleteModelVisible(false);
        setSelectedModelIds([]);
        project?.refetch();
        modelData.refetch();
    };

    const handleOnRemoveTag = (tagId: string) => {
        setselectedTagId(tagId);
        setDeleteProjectTagVisible(true);
    };

    const handleOnRemoveHealthCheck = (healthCheckId: string) => {
        setSelectedHealthCheckId(healthCheckId);
        setDeleteProjectHealthCheckVisible(true);
    };

    const isAdmin = project?.data?.Project?.isAdmin || false;
    const currentUser = useCurrentUser();
    const isGlobalAdmin = currentUser?.role === UserRoleEnum.Admin;

    const clientData = useGetClientQuery({
        variables: { id: project?.data?.Project?.clientId },
        skip: !project?.data?.Project?.clientId,
    });
    const isClientAdmin = clientData?.data?.Client?.isAdmin || false;
    const canEditProject = isAdmin || isClientAdmin;

    const tabs = [
        { id: 'models' as const, label: 'Models', icon: Box },
        { id: 'standards' as const, label: 'Standards', icon: BookOpen, badge: 'Preview' },
        ...((isAdmin || isClientAdmin || isGlobalAdmin) ? [
            { id: 'reports' as const, label: 'Reports', icon: BarChart3 },
        ] : []),
        ...(isGlobalAdmin ? [
            { id: 'worksets' as const, label: 'Worksets', icon: Package },
            { id: 'standard-details' as const, label: 'Standard Details', icon: FileText },
        ] : []),
    ];

    return (
        <div className="flex flex-col h-full">
            {/* Admin toolbar */}
            <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={canEditProject}>
                <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <button
                        className="btn btn-ghost text-sm"
                        disabled={togglePrivateResult.loading}
                        onClick={togglePrivateHandle}
                    >
                        {project?.data?.Project?.isPrivate === true
                            ? <><EyeOff size={15} /> Private</>
                            : <><Eye size={15} /> Public</>
                        }
                    </button>
                    <button className="btn btn-ghost text-sm" onClick={() => setEditProjectVisible(true)}>
                        <Pencil size={15} /> Edit
                    </button>
                </div>
            </ShowOnRole>

            {/* Project context strip: client, library, description, tags */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pt-3">
                {/* Client & Library links */}
                {project?.data?.Project?.client?.name && (
                    <Link
                        to={`/clients/${project?.data?.Project?.client?.id}`}
                        className="card px-3 py-1.5 text-xs font-medium hover:opacity-80 transition-opacity flex items-center gap-1.5"
                        style={{ color: 'var(--accent-text)', textDecoration: 'none' }}
                    >
                        <span style={{ color: 'var(--text-tertiary)' }}>Client</span>
                        {project.data.Project.client.name}
                    </Link>
                )}
                {project?.data?.Project?.library?.name && (
                    <Link
                        to={`/clients/${project?.data?.Project?.client?.id}/library/${project?.data?.Project?.library?.id}`}
                        className="card px-3 py-1.5 text-xs font-medium hover:opacity-80 transition-opacity flex items-center gap-1.5"
                        style={{ color: 'var(--accent-text)', textDecoration: 'none' }}
                    >
                        <span style={{ color: 'var(--text-tertiary)' }}>Library</span>
                        {project.data.Project.library.name}
                    </Link>
                )}

                {/* Description */}
                {project?.data?.Project?.description && (
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {project.data.Project.description}
                    </span>
                )}

                <div className="flex-1" />

                {/* Tags */}
                {project?.data?.Project?.tags && project.data.Project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {project.data.Project.tags.filter(t => t).map(t => (
                            <TagBadge key={t!.id || newGuid()} tag={t as Tag} className="inline-flex items-center gap-1 text-[11px]">
                                <span className="font-semibold">{(t as Tag).type}:</span>
                                <span>{t!.name}</span>
                                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                    <button
                                        onClick={() => handleOnRemoveTag(t?.id || '')}
                                        className="ml-0.5 rounded-full hover:opacity-70"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                                    >
                                        <Trash2 size={9} />
                                    </button>
                                </ShowOnRole>
                            </TagBadge>
                        ))}
                    </div>
                )}

                {/* Health Check definitions */}
                {project?.data?.Project?.healthChecks && project.data.Project.healthChecks.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {project.data.Project.healthChecks.filter(t => t).map(t => (
                            <span key={t!.id || newGuid()} className="badge-success inline-flex items-center gap-1 text-[11px]">
                                {t!.name}
                                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                    <button
                                        onClick={() => handleOnRemoveHealthCheck(t?.id || '')}
                                        className="ml-0.5 rounded-full hover:opacity-70"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                                    >
                                        <Trash2 size={9} />
                                    </button>
                                </ShowOnRole>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Health summary cards */}
            {projectHealthSummary.modelsWithHealth > 0 && (
                <div className="flex flex-wrap gap-3 px-4 pt-3">
                    <StatCard label="Health Score" value={`${projectHealthSummary.overallScore}%`} icon={Activity} color="var(--accent)" />
                    <StatCard label="Passing" value={projectHealthSummary.totalPassing} icon={CheckCircle2} color="var(--success)" />
                    <StatCard label="Warnings" value={projectHealthSummary.totalWarning} icon={AlertTriangle} color="var(--warning)" />
                    <StatCard label="Failing" value={projectHealthSummary.totalFailing} icon={XCircle} color="var(--danger)" />
                    <StatCard label="Models" value={`${projectHealthSummary.modelsWithHealth} / ${projectHealthSummary.totalModels}`} icon={Box} color="var(--info)" />
                </div>
            )}

            {/* Tab bar */}
            <div className="px-4 pt-3 pb-1">
                <div className="tab-bar" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={activeTab === tab.id ? 'tab-item-active' : 'tab-item'}
                            >
                                <Icon size={14} strokeWidth={1.5} />
                                {tab.label}
                                {'badge' in tab && tab.badge && (
                                    <span
                                        className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                                        style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)', letterSpacing: '0.04em', lineHeight: 1 }}
                                    >
                                        {tab.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'models' && (
                    <div className="flex flex-col h-full overflow-hidden">
                        <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                            <RefreshButton onRefresh={() => modelData.refetch()} />
                            <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={canEditProject}>
                                {selectedModelIds.length > 0 && (
                                    <>
                                        <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                                        <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteModelVisible(true)}>
                                            <Trash2 size={15} /> Delete ({selectedModelIds.length})
                                        </button>
                                    </>
                                )}
                            </ShowOnRole>
                        </div>
                        <div className="flex flex-col flex-1 overflow-hidden">
                            <DataTableNew
                                sortingMode="server"
                                sort={modelSort}
                                onSortChange={setModelSort}
                                isLoading={modelData.loading}
                                data={modelData.data?.ModelList?.nodes as any[]}
                                link="../model"
                                onSearchChanged={value => setSearchModel(value)}
                                onSelected={(ids) => setSelectedModelIds(ids)}
                                fetchMore={() => modelData.data?.ModelList?.pageInfo?.hasNextPage && modelData.fetchMore({
                                    variables: {
                                        projectId: project?.data?.Project?.id,
                                        contains: searchModel,
                                        after: modelData.data?.ModelList?.pageInfo?.endCursor,
                                        order: createGraphQLOrder<ModelSortInput>(modelSort),
                                    },
                                    updateQuery: (previousResult, { fetchMoreResult }) => {
                                        if (!fetchMoreResult) return previousResult;
                                        const prev = previousResult.ModelList?.nodes || [];
                                        const next = fetchMoreResult.ModelList?.nodes || [];
                                        return {
                                            ...previousResult,
                                            ModelList: {
                                                ...fetchMoreResult.ModelList,
                                                nodes: [...prev, ...next],
                                                pageInfo: fetchMoreResult.ModelList?.pageInfo as any
                                            }
                                        };
                                    }
                                })}
                                columns={[
                                    { id: 'applicationName', name: 'Application', sortField: 'applicationName' },
                                    { id: 'customProperties', name: 'Custom Properties', render: (row: any) => <CustomPropertiesCell row={row} /> },
                                    {
                                        id: 'healthResults', name: 'Health', width: '200px', render: (row: any) => {
                                            const summary = modelSummaries.find(s => s.modelId === row.id);
                                            if (!summary) return <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</span>;
                                            return (
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                                                        <span style={{ color: 'var(--success)' }}>{summary.passing}✓</span>
                                                        <span style={{ color: 'var(--warning)' }}>{summary.warning}⚠</span>
                                                        <span style={{ color: 'var(--danger)' }}>{summary.failing}✗</span>
                                                    </div>
                                                    <ScoreBadge score={summary.score} />
                                                </div>
                                            );
                                        }
                                    },
                                    { id: 'updatedTime', name: 'Updated Date', width: '120px', sortField: 'updatedTime', render: (row: any) => <>{ticksToDate(row.updatedTime)}</> },
                                    ...(canEditProject ? [{
                                        id: 'remove', name: '', width: '56px', render: (row: any) => (
                                            <button
                                                className="btn btn-ghost p-1"
                                                style={{ color: 'var(--danger)' }}
                                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setSelectedModelId(row.id); setDeleteModelVisible(true); }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )
                                    }] : [])
                                ]}
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'standards' && (
                    <Standards
                        projectId={project?.data?.Project?.id ?? ""}
                        libraryId={project?.data?.Project?.libraryId ?? undefined}
                        clientName={clientData?.data?.Client?.name ?? undefined}
                        clientDefaultLibraryId={clientData?.data?.Client?.defaultLibraryId ?? undefined}
                        canEdit={isGlobalAdmin || isAdmin}
                        canUsePreviewFeatures={isGlobalAdmin}
                    />
                )}

                {activeTab === 'reports' && (
                    <Reports projectId={project?.data?.Project?.id ?? ""} />
                )}

                {activeTab === 'worksets' && (
                    <Worksets
                        isAdmin={isAdmin}
                        projectId={project?.data?.Project?.id || ""}
                        isLoading={false}
                        worksets={project?.data?.Project?.worksets || []}
                        refresh={() => project?.refetch()}
                    />
                )}

                {activeTab === 'standard-details' && (
                    <StandardDetailsTab
                        isAdmin={isAdmin}
                        projectId={project?.data?.Project?.id ?? ""}
                        libraryId={project?.data?.Project?.libraryId ?? ""}
                        onRefresh={() => project?.refetch()}
                        loading={project?.loading}
                        contents={project?.data?.Project?.approvedContents || []}
                    />
                )}
            </div>

            {editProjectVisible && <EditProjectDialog key={newGuid()} projectId={project?.data?.Project?.id || ""} visible={editProjectVisible} onClose={handleOnClose} />}
            {deleteProjectTagVisible && <DeleteProjectTagDialog key={newGuid()} id={project?.data?.Project?.id || ""} tagId={selectedTagId} visible={deleteProjectTagVisible} onClose={handleOnClose} />}
            {deleteProjectHealthCheckVisible && <DeleteProjectHealthChecksDialog key={newGuid()} id={project?.data?.Project?.id || ""} healthCheckId={selectedHealthCheckId} visible={deleteProjectHealthCheckVisible} onClose={handleOnClose} />}
            {deleteModelVisible && <DeleteModelsDialog key={newGuid()} ids={selectedModelIds.length > 0 ? selectedModelIds : [selectedModelId]} visible={deleteModelVisible} onClose={handleOnClose} />}
        </div>
    );
};

export default ProjectOverview;
